<template>
  <div>
    <v-container>
      <v-row>
        <v-col cols="12">
          <div class="detail-page__back" @click="router.back()">
            <v-icon size="18">mdi-arrow-left</v-icon>
            <span>Back to Submissions</span>
          </div>
          <h1 class="text-primary weight-600 pb-4">{{ pageTitle }}</h1>
          <v-alert
            v-if="showReimportBanner"
            type="warning"
            variant="tonal"
            prominent
            class="mb-4"
            data-testid="cmm-reimport-banner">
            <template #prepend>
              <v-icon size="large">mdi-account-alert</v-icon>
            </template>
            <div class="text-body-1 font-weight-bold mb-1">Couldn't access this CMM submission</div>
            <div class="text-body-2 mb-3">
              CoverMyMeds rejected the patient's last name or date of birth. Reimport with the corrected details to
              continue.
            </div>
            <template #append>
              <v-btn color="warning" variant="flat" :disabled="lockViewerMode" @click="goToReimport">
                Reimport with corrected info
              </v-btn>
            </template>
          </v-alert>
          <v-alert
            v-else-if="showImportInProgressBanner"
            type="info"
            variant="tonal"
            prominent
            class="mb-4"
            data-testid="cmm-import-in-progress-banner">
            <template #prepend>
              <v-icon size="large">mdi-progress-clock</v-icon>
            </template>
            <div class="text-body-1 font-weight-bold mb-1">Import in progress</div>
            <div class="text-body-2">
              We're fetching this submission from CoverMyMeds. Check back shortly — you'll be emailed if the patient
              details don't match.
            </div>
          </v-alert>
          <div v-if="preloader">
            <SubmissionPageSkeleton />
          </div>
          <div v-else>
            <ReadOnlyBanner v-if="isReadOnlyDevice" />
            <ReadOnlyBanner v-if="lockViewerMode" icon="mdi-account-lock-outline" :text="lockBannerText">
              <template v-if="lockFree" #action>
                <v-btn size="small" color="primary" variant="flat" @click="takeOverEditing">Take over editing</v-btn>
              </template>
            </ReadOnlyBanner>
            <v-row class="submission-status-wrapper">
              <v-col cols="12" class="lh--available-orders-container">
                <SubmissionDetailCard
                  :order="orderInfo"
                  :buttons-visible="false"
                  :readonly="isProvider"
                  :fax-forms="faxDestinationOptions" />
              </v-col>
            </v-row>
            <v-row class="submission-status-wrapper">
              <v-col cols="12" class="lh--available-orders-container">
                <PolicyReviewCard :order="orderInfo" />
              </v-col>
            </v-row>
            <v-row v-if="orderInfo" class="submission-status-wrapper">
              <v-col cols="12" class="lh--available-orders-container">
                <DrugIndicationCard :order="orderInfo" :readonly="isProvider" />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                class="submission-detail-section"
                :class="{ 'lh-readonly-fields': isReadOnlyDevice || lockViewerMode }">
                <AllFormStepsView
                  ref="allFormStepsRef"
                  :disabled="isDisabled || isReadOnlyDevice || lockViewerMode"
                  :filled-form-pdf-blob="filledFormPdfBlob"
                  :archivable="!lockViewerMode"
                  :actions-visible="!isProvider"
                  @send-to-prescriber="handleSendToPrescriber"
                  @send-to-plan="handleSendToPlan"
                  @appeal-sent="onAppealSent"
                  @archive="showArchiveDialog = true"
                  @unarchive="showUnarchiveDialog = true" />
              </v-col>
            </v-row>
            <v-row v-if="orderInfo && !isProvider">
              <v-col cols="12" :inert="lockViewerMode">
                <IntakeTagBar
                  :tags="orderInfo.statusTags || []"
                  :tag-defs="paAvailableTags"
                  :tag-due-dates="orderInfo.tagDueDates || []"
                  :sort-order="tagSortOrder"
                  entity-type="submission"
                  :entity-id="orderInfo.orderKey"
                  @update:tags="(t) => (orderInfo.statusTags = t)"
                  @saved="auditLogListRef?.showLatest()"
                  @update:tag-due-dates="(d) => (orderInfo.tagDueDates = d)" />
                <AssigneeBar
                  :assignees="orderInfo.assignees || []"
                  entity-type="submission"
                  :entity-id="orderInfo.orderKey"
                  @update:assignees="(a) => (orderInfo.assignees = a)" />
                <GroupBar
                  :assigned-groups="orderInfo.assignedGroups || []"
                  entity-type="submission"
                  :entity-id="orderInfo.orderKey"
                  @update:assigned-groups="(g) => (orderInfo.assignedGroups = g)" />
                <SubstatusBar entity-type="submission" :entity-id="orderInfo.orderKey" />
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>

      <v-row v-if="(showFaxEditor || showEmailEditor) && !isReadOnlyDevice && !lockViewerMode">
        <v-col v-if="orderInfo" cols="12">
          <!-- Send Method Toggle (only shown when email is enabled) -->
          <v-btn-toggle
            v-if="sendEmailEnabled && orderInfo?.submissionMethod !== 'eFax'"
            v-model="sendMethod"
            mandatory
            class="mb-4"
            color="primary"
            variant="outlined">
            <v-btn value="fax">
              <v-icon start>mdi-fax</v-icon>
              Send Fax
            </v-btn>
            <v-btn value="email">
              <v-icon start>mdi-email</v-icon>
              Send Email
            </v-btn>
          </v-btn-toggle>

          <!-- Fax Editor -->
          <OutgoingFaxEditor
            v-if="sendMethod === 'fax'"
            ref="outgoingFaxEditorRef"
            :types="[]"
            :order-info="orderInfo"
            :to-fax-number="currentFaxNumber"
            :attachment-options="attachmentOptions"
            :frontend-template="currentTemplate"
            :preselected-attachment="preselectedFilledFormAttachment"
            :send-button-text="sendButtonText"
            :defer-send="currentFaxAction === 'prescriber'"
            :supporting-documents="supportingDocuments"
            :attach-supporting-docs-fax-enabled="attachSupportingDocsFaxEnabled"
            title="Send Fax"
            @on-complete="onFaxComplete"
            @on-pre-send="saveOrderDraft"
            @on-signature-redraw-required="focusSignatureSection"
            @on-frontend-template-selected="handlePrescriberTemplateSelected" />

          <!-- Email Editor -->
          <OutgoingEmailEditor
            v-if="sendMethod === 'email'"
            ref="outgoingEmailEditorRef"
            :types="[]"
            :order-info="orderInfo"
            :to-emails="currentEmailAddresses"
            :default-subject="currentEmailSubject"
            :attachment-options="attachmentOptions"
            :frontend-template="currentTemplate"
            :preselected-attachment="preselectedFilledFormAttachment"
            :send-button-text="emailSendButtonText"
            :defer-send="currentFaxAction === 'prescriber'"
            :supporting-documents="supportingDocuments"
            title="Send Email"
            @on-complete="onEmailComplete"
            @on-pre-send="saveOrderDraft"
            @on-frontend-template-selected="handlePrescriberTemplateSelected" />
        </v-col>
        <v-alert v-if="showFaxHelpText && sendMethod === 'fax'" type="info" variant="tonal" prominent class="mb-4">
          <template #prepend>
            <v-icon size="large">mdi-information</v-icon>
          </template>
          <div class="text-body-1 font-weight-bold">
            Please verify this is the correct fax number for the insurance provider where the prior authorization will
            be sent.
          </div>
        </v-alert>
      </v-row>

      <v-row v-if="!isProvider">
        <v-col cols="12">
          <AuditLogList
            ref="auditLogListRef"
            :order-info-id="route.params.id"
            :tag-defs="paAvailableTags"
            @mentions-sent="onMentionsSent" />
        </v-col>
      </v-row>

      <ConfirmDialog
        v-model="showArchiveDialog"
        title="Archive Submission"
        message="Are you sure you want to archive this submission?"
        confirm-text="Archive"
        variant="warning"
        show-input
        input-label="Archive reason (optional)"
        :loading="archiveLoading"
        @confirm="onArchive"
        @cancel="showArchiveDialog = false" />

      <ConfirmDialog
        v-model="showUnarchiveDialog"
        title="Unarchive Submission"
        message="Are you sure you want to unarchive this submission?"
        confirm-text="Unarchive"
        :loading="archiveLoading"
        @confirm="onUnarchive"
        @cancel="showUnarchiveDialog = false" />

      <v-snackbar v-model="showSuccessNotification" color="success" timeout="5000" location="top">
        {{ successNotificationMessage }}
      </v-snackbar>
      <div v-if="errors.length > 0" class="mt-4">
        <v-alert v-for="error in errors" :key="error" type="error" variant="tonal" class="mb-2">
          {{ error }}
        </v-alert>
      </div>
    </v-container>
  </div>
</template>

<script setup>
import AllFormStepsView from "@/pages/AllFormStepsView";
import SubmissionDetailCard from "@/components/SubmissionDetailCard.vue";
import ReadOnlyBanner from "@/components/dashboard/ReadOnlyBanner.vue";
import { useResponsive } from "@/composables/useResponsive";
import SubmissionPageSkeleton from "@/components/SubmissionPageSkeleton.vue";
import AuditLogList from "@/components/AuditLogList.vue";
import OutgoingFaxEditor from "@/components/OutgoingFaxEditor.vue";
import OutgoingEmailEditor from "@/components/OutgoingEmailEditor.vue";
import IntakeTagBar from "@/components/dashboard/IntakeTagBar.vue";
import AssigneeBar from "@/components/dashboard/AssigneeBar.vue";
import GroupBar from "@/components/dashboard/GroupBar.vue";
import SubstatusBar from "@/components/dashboard/SubstatusBar.vue";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog.vue";

import { onBeforeMount, ref, computed, nextTick } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { clearOrderInfoStorage, convertDateFormat, formatLockHolderName, tryParseApiErrors } from "@/utils";

import { orderInfoService } from "@/services/orderInfoService";
import { userNotesService } from "@/services/userNotesService";
import { mainServices } from "@/services/mainServices";
import { useOrderInfoStore } from "@/stores/orderInfoStore";
import { useAuthStore } from "@/stores";

import PolicyReviewCard from "@/components/PolicyReviewCard.vue";
import DrugIndicationCard from "@/components/DrugIndicationCard.vue";

const { orderInfo } = storeToRefs(useOrderInfoStore());
const authStore = useAuthStore();
// Provider logins (LAM-5263) read this page as a pure viewer: no lock, no staff time, no polling.
const isProvider = computed(() => Boolean(authStore.userInfo?.isProvider));
const route = useRoute();
const router = useRouter();
const { isReadOnlyDevice } = useResponsive();

// Client config for feature flags
const clientConfig = ref(null);
const sendEmailEnabled = computed(() => clientConfig.value?.uiConfig?.intake?.sendEmailEnabled ?? false);
const paAvailableTags = computed(() => clientConfig.value?.uiConfig?.priorAuth?.availableTags || []);
const tagSortOrder = computed(() => clientConfig.value?.uiConfig?.tagSortOrder ?? null);

const onAppealSent = () => {
  if (orderInfo.value?.orderKey) loadOrder(orderInfo.value.orderKey);
};
const onMentionsSent = (assigned) => {
  if (!orderInfo.value) return;
  const current = orderInfo.value.assignees || [];
  const ids = new Set(current.map((a) => a.id));
  orderInfo.value.assignees = [...current, ...assigned.filter((a) => !ids.has(a.id))];
};
const attachSupportingDocsFaxEnabled = computed(
  () => clientConfig.value?.uiConfig?.priorAuth?.attachSupportingDocsFaxEnabled ?? false
);

const nonEditableStatuses = [
  "Created",
  "Processing",
  "Sent to prescriber",
  "Sent to plan",
  "No payor response received",
  "Requires more information",
  "Approved",
  "Prior authorization not required",
  "Denied",
  "Archived",
];

const isDisabled = computed(() => {
  if (orderInfo.value?.status) {
    if (orderInfo.value.staffEditOverride) return false;
    // CMM orders in Requires More Information are re-openable (LAM-4515); fax ones stay locked.
    if (orderInfo.value.status === "Requires more information" && orderInfo.value.submissionMethod === "CoverMyMeds") {
      return false;
    }
    return nonEditableStatuses.includes(orderInfo.value.status);
  }
  return false;
});

const showReimportBanner = computed(() => !!orderInfo.value?.orderSubmission?.isDemographicsReimportEligible);
const showImportInProgressBanner = computed(() => !!orderInfo.value?.orderSubmission?.isCmmImportInProgress);

const goToReimport = () => {
  const key = orderInfo.value?.orderSubmission?.portalSubmissionId;
  router.push({ name: "import-cmm-submission", query: key ? { key } : {} });
};

// Opening an existing order starts in the skeleton: the lock acquisition awaited before loadOrder()
// otherwise paints the detail cards empty for its whole round-trip (LAM-5432).
const preloader = ref(!!route.params.id);
const pageTitle = ref("Review Submission");
const faxDestinationOptions = ref([]);
const faxAttachments = ref([]);
const showSuccessNotification = ref(false);
const auditLogListRef = ref(null);
const allFormStepsRef = ref(null);
const outgoingFaxEditorRef = ref(null);
const filledFormPdfBlob = ref(null);
const errors = ref([]);
const showFaxEditor = ref(false);
const showEmailEditor = ref(false);
const currentFaxAction = ref(null); // 'prescriber' or 'plan'
const currentFaxNumber = ref(null);
const currentTemplate = ref(null);
const sendButtonText = ref("Send Fax");
const showFaxHelpText = ref(false);
const outgoingEmailEditorRef = ref(null);
const currentEmailAddresses = ref([]);
const currentEmailSubject = ref("");
const sendMethod = ref("fax"); // 'fax' or 'email'
const successNotificationMessage = ref("Fax sent successfully!");
const showArchiveDialog = ref(false);
const showUnarchiveDialog = ref(false);
const archiveLoading = ref(false);

async function onArchive(reason) {
  if (lockViewerMode.value || archiveLoading.value) return;
  const orderKey = orderInfo.value.orderKey;
  archiveLoading.value = true;
  try {
    await orderInfoService.archiveOrder(orderKey);
  } catch (e) {
    errors.value = tryParseApiErrors(e);
    archiveLoading.value = false;
    return;
  }
  showArchiveDialog.value = false;
  successNotificationMessage.value = "Submission archived";
  showSuccessNotification.value = true;
  let noteFailed = false;
  const note = reason?.trim();
  if (note) {
    try {
      await userNotesService.createNote({ orderInfo: orderKey, note, level: "archive_reason" });
    } catch (e) {
      noteFailed = true;
    }
  }
  await loadOrder(orderKey);
  auditLogListRef.value?.showLatest();
  if (noteFailed) errors.value = ["The submission was archived, but saving the archive reason failed."];
  archiveLoading.value = false;
}

async function onUnarchive() {
  if (lockViewerMode.value || archiveLoading.value) return;
  const orderKey = orderInfo.value.orderKey;
  archiveLoading.value = true;
  try {
    await orderInfoService.unarchiveOrder(orderKey);
  } catch (e) {
    errors.value = tryParseApiErrors(e);
    archiveLoading.value = false;
    return;
  }
  showUnarchiveDialog.value = false;
  successNotificationMessage.value = "Submission unarchived";
  showSuccessNotification.value = true;
  await loadOrder(orderKey);
  auditLogListRef.value?.showLatest();
  archiveLoading.value = false;
}

const emailSendButtonText = computed(() => {
  if (currentFaxAction.value === "prescriber") {
    return "Send to prescriber";
  }
  return "Send Email";
});

const attachmentOptions = computed(() => {
  const options = [];

  // Response faxes from faxAttachments
  faxAttachments.value.forEach((a, index) => {
    options.push({
      title: `Response Fax ${index + 1}: ${a.label} ${
        a.timestamp ? "Received on " + convertDateFormat(a.timestamp) : ""
      }`,
      value: {
        type: "response_fax",
        s3_url: a.link,
      },
    });
  });

  // Flattened form (for OrderInfo only)
  if (
    orderInfo.value?.editableSubmissionForm &&
    orderInfo.value?.submissionMethod === "eFax" &&
    orderInfo.value?.orderKey
  ) {
    options.push({
      title: orderInfo.value?.faxDescription ? "Filled " + orderInfo.value?.faxDescription : "Filled Prior Auth Form",
      value: {
        type: "flattened_form",
        s3_url: null,
      },
    });
  }

  return options;
});

const supportingDocuments = computed(() => {
  const urls = [];

  // Patient chart URLs: orderInfo.patientChart[].links[0] (if available)
  if (orderInfo.value?.patientChart && Array.isArray(orderInfo.value.patientChart)) {
    orderInfo.value.patientChart.forEach((chart) => {
      if (chart.links && Array.isArray(chart.links) && chart.links.length > 0) {
        urls.push(chart.links[0]);
      }
    });
  }

  // Medical necessity URLs: orderInfo.medicalNecessity (if available)
  if (orderInfo.value?.medicalNecessity && Array.isArray(orderInfo.value.medicalNecessity)) {
    orderInfo.value.medicalNecessity.forEach((url) => {
      if (url) {
        urls.push(url);
      }
    });
  }

  return urls;
});

const incompleteStatuses = ["Created", "Incomplete", "Review demographics info"];
const reviewStatuses = ["Requires review", "Review clinical info"];

const pageTitleFor = (status) =>
  incompleteStatuses.includes(status)
    ? "Review Submission and Send to Processing"
    : reviewStatuses.includes(status)
    ? "Review Submission and Send to Plan"
    : "Review Submission";

const fetchPrescriberTemplate = async () => {
  if (!orderInfo.value?.orderKey) return null;
  try {
    const data = await orderInfoService.getPrescriberTemplate(orderInfo.value.orderKey);
    return {
      name: data.name,
      content: data.content || "",
      category: "prescriberTemplates",
      templateId: data.templateId || null,
      variant: data.templateVariant || null,
    };
  } catch {
    return null;
  }
};

const preselectedFilledFormAttachment = ref(null);
let lockInterval = null;
let pollInterval = null;
const lockViewerMode = ref(false);
const lockHolder = ref(null);
const lockFree = ref(false);
const wasEditor = ref(false);

const lockBannerText = computed(() => {
  if (isProvider.value) return "You have view-only access to this submission.";
  if (lockFree.value) return "The record is free — you can take over editing.";
  const name = formatLockHolderName(lockHolder.value);
  return name ? `${name} is editing this record — view only.` : "Another user is editing this record — view only.";
});

const enterViewerMode = (lock) => {
  stopLockInterval();
  lockViewerMode.value = true;
  lockFree.value = false;
  lockHolder.value = lock?.user || null;
  showArchiveDialog.value = false;
  showUnarchiveDialog.value = false;
};

const demoteIfLockRejected = (e) => {
  if (e?.response?.status !== 423) return false;
  enterViewerMode(e.response.data?.lock);
  errors.value = ["This record is now being edited by another user."];
  return true;
};

onBeforeMount(async () => {
  if (route.params.id) {
    clearOrderInfoStorage();
    if (isProvider.value) {
      lockViewerMode.value = true;
    } else {
      try {
        await orderInfoService.acquireOrderLock(route.params.id);
        startLockInterval();
        startStaffTime(route.params.id);
        wasEditor.value = true;
      } catch (e) {
        if (e.response?.status !== 423) {
          errors.value = tryParseApiErrors(e);
          // If the redirect is delayed or canceled, the page must show the error, not a stuck skeleton.
          preloader.value = false;
          router.push({ name: "prior-auth" });
          return;
        }
        enterViewerMode(e.response.data?.lock);
      }
      startPollInterval();
    }
    loadOrder(route.params.id);
  }
  getFaxForms();
  window.addEventListener("beforeunload", onBeforeUnload);
  // Fetch client config for feature flags (a company-less provider has none to fetch)
  if (!isProvider.value) {
    try {
      clientConfig.value = await mainServices.getCurrentUserClientConfig();
    } catch (e) {
      // Suppress - email feature will be disabled if config fails to load
    }
  }
});

async function getFaxForms() {
  faxDestinationOptions.value = await mainServices.getFaxForms();
}

async function loadOrder(orderKey) {
  preloader.value = true;
  errors.value = [];
  try {
    const fetchedOrder = await orderInfoService.getOrderDetails(orderKey);
    orderInfo.value = fetchedOrder.orderInfo;
    faxAttachments.value = fetchedOrder.orderInfo.orderSubmission?.responseDocuments || [];
    pageTitle.value = pageTitleFor(orderInfo.value.status);

    // Fetch filled form PDF if submission is not editable and has editableSubmissionForm
    if (orderInfo.value?.editableSubmissionForm && nonEditableStatuses.includes(orderInfo.value.status)) {
      try {
        const pdfResponse = await orderInfoService.getFilledFormPdf(orderKey);
        filledFormPdfBlob.value = pdfResponse.data;
      } catch (err) {
        // Suppress error - filled form might not be available yet
      }
    }
  } catch (err) {
    errors.value = tryParseApiErrors(err);
  }
  preloader.value = false;
}

const onBeforeUnload = () => {
  stopPollInterval();
  if (lockViewerMode.value) {
    if (wasEditor.value) updateStaffTime();
    return;
  }
  releaseLockOnUnload();
  updateStaffTime();
};

onBeforeRouteLeave(async () => {
  stopPollInterval();
  if (lockViewerMode.value) {
    window.removeEventListener("beforeunload", onBeforeUnload);
    if (wasEditor.value) await updateStaffTime();
    return;
  }
  await releaseLock();
  await updateStaffTime();
});

const startStaffTime = (orderKey) => {
  try {
    orderInfoService.startOrderStaffTime(orderKey);
  } catch (e) {
    // Suppress
  }
};

const updateStaffTime = async () => {
  const orderKey = route.params.id;
  try {
    window.removeEventListener("beforeunload", onBeforeUnload);
    await orderInfoService.pushOrderStaffTime(orderKey);
  } catch (e) {
    // Suppress
  }
};

const releaseLock = async () => {
  const orderKey = route.params.id;
  stopLockInterval();
  try {
    await orderInfoService.releaseOrderLock(orderKey);
  } catch (e) {
    // Suppress
  }
};

const releaseLockOnUnload = () => {
  stopLockInterval();
  orderInfoService.releaseOrderLockOnUnload(route.params.id);
};

const startLockInterval = () => {
  if (lockInterval) return;
  lockInterval = setInterval(async () => {
    try {
      await orderInfoService.acquireOrderLock(route.params.id);
    } catch (e) {
      // A 423 renew means the lock lapsed and someone else took over; other errors are transient.
      demoteIfLockRejected(e);
    }
  }, 60000);
};

const stopLockInterval = () => {
  clearInterval(lockInterval);
  lockInterval = null;
};

// One 15-s tick carries both jobs: log refresh for everyone, lock peek for viewers.
const startPollInterval = () => {
  if (pollInterval) return;
  pollInterval = setInterval(onPollTick, 15000);
};

const stopPollInterval = () => {
  clearInterval(pollInterval);
  pollInterval = null;
};

const onPollTick = async () => {
  if (document.hidden) return;
  if (lockViewerMode.value) {
    try {
      const lock = await orderInfoService.getOrderLock(route.params.id);
      if (lock) {
        lockHolder.value = lock.user || null;
        lockFree.value = false;
      } else {
        lockFree.value = true;
      }
    } catch (e) {
      // Transient peek failure — try again next tick
    }
  }
  if (orderInfo.value?.editableSubmissionForm?.status === "Processing") {
    await refreshFormWhenAutofillLands();
  }
  auditLogListRef.value?.refresh();
};

const refreshFormWhenAutofillLands = async () => {
  try {
    const fetched = await orderInfoService.getOrderDetails(route.params.id);
    const form = fetched.orderInfo?.editableSubmissionForm;
    if (!form || form.status === "Processing") return;
    orderInfo.value.editableSubmissionForm = form;
    orderInfo.value.status = fetched.orderInfo.status;
    pageTitle.value = pageTitleFor(orderInfo.value.status);
  } catch (e) {
    // Transient fetch failure — try again next tick
  }
};

const takeOverEditing = async () => {
  try {
    await orderInfoService.acquireOrderLock(route.params.id);
  } catch (e) {
    // Lost the takeover race — stay a viewer with the new holder's name.
    if (e.response?.status === 423) enterViewerMode(e.response.data?.lock);
    else errors.value = ["Could not take over editing — try again."];
    return;
  }
  lockViewerMode.value = false;
  lockFree.value = false;
  lockHolder.value = null;
  errors.value = [];
  startLockInterval();
  if (!wasEditor.value) startStaffTime(route.params.id);
  wasEditor.value = true;
  await loadOrder(route.params.id);
};

const saveOrderDraft = async (resolve, reject, { previewOnly = false } = {}) => {
  try {
    // Previewing only needs the current form edits (signature, checkboxes, fields) persisted so
    // the fax renders from up-to-date data — it must never create an order or advance formStep,
    // both of which are send-only side effects. If there's no order yet, there's nothing to render.
    if (previewOnly) {
      if (!orderInfo.value.orderKey) {
        reject(new Error("Order must be saved before it can be previewed"));
        return;
      }
      if (allFormStepsRef.value?.saveDraft) {
        await allFormStepsRef.value.saveDraft();
      }
      resolve();
      return;
    }
    // For "Send to plan" action, save orderInfo with formStep="Send to Plan" before sending fax
    if (currentFaxAction.value === "plan") {
      // Ensure PDF form is merged if needed
      if (orderInfo.value.editableSubmissionForm && allFormStepsRef.value?.saveDraft) {
        await allFormStepsRef.value.saveDraft();
      }

      // Save orderInfo with formStep="Send to Plan" (same as performSubmission)
      if (!orderInfo.value.orderKey) {
        const newOrder = await orderInfoService.createEmptyOrder(orderInfo.value);
        orderInfo.value.orderKey = newOrder.orderKey;
      }

      const orderData = {
        ...orderInfo.value,
        formStep: "Send to Plan",
      };

      await orderInfoService.saveOrder(orderInfo.value.orderKey, orderData);
    } else {
      // For other actions (prescriber), just save draft
      if (allFormStepsRef.value?.saveDraft) {
        await allFormStepsRef.value.saveDraft();
      }
    }
    resolve();
  } catch (error) {
    demoteIfLockRejected(error);
    reject(error);
  }
};

const preselectFilledFormAttachment = () => {
  // Pre-select filled form attachment (used for both prescriber and plan actions)
  if ((orderInfo.value?.editableSubmissionForm && orderInfo.value?.orderKey) || filledFormPdfBlob.value) {
    preselectedFilledFormAttachment.value = {
      title: orderInfo.value?.faxDescription ? "Filled " + orderInfo.value?.faxDescription : "Filled Prior Auth Form",
      value: {
        type: "flattened_form",
        s3_url: null,
      },
    };
  }
};

const handlePrescriberTemplateSelected = () => {
  // When prescriber template is selected, pre-select the attachment if eFax
  if (orderInfo.value?.submissionMethod === "eFax") {
    preselectFilledFormAttachment();
  }
};

const buildPrescriberOutboundFromFax = (requestData) => ({
  method: "fax",
  body: requestData.text || "",
  toFaxNumber: requestData.toFaxNumbers?.[0],
  fileLinks: requestData.fileLinks || [],
  attachment: requestData.attachment || null,
  templateId: currentTemplate.value?.templateId ?? null,
  templateVariant: currentTemplate.value?.variant ?? null,
});

const buildPrescriberOutboundFromEmail = (requestData) => ({
  method: "email",
  body: requestData.body || "",
  toEmails: requestData.toEmails || [],
  subject: requestData.subject || "",
  fileLinks: [],
  attachment: null,
  templateId: currentTemplate.value?.templateId ?? null,
  templateVariant: currentTemplate.value?.variant ?? null,
});

const prescriberQueuedNotificationMessage = (submissionMethod, notifyMethod) => {
  if (submissionMethod === "CoverMyMeds") {
    return "Prescriber will be notified after answers are synced to CoverMyMeds.";
  }
  if (notifyMethod === "email") {
    return "Prescriber will be notified by email shortly.";
  }
  return "Prescriber will be notified by fax shortly.";
};

const queuePrescriberNotification = async (outbound) => {
  try {
    await orderInfoService.sendToPrescriber(orderInfo.value.orderKey, {
      pendingPrescriberOutbound: outbound,
    });
    successNotificationMessage.value = prescriberQueuedNotificationMessage(
      orderInfo.value?.submissionMethod,
      outbound.method
    );
    showSuccessNotification.value = true;
    showFaxEditor.value = false;
    showEmailEditor.value = false;
    await loadOrder(orderInfo.value.orderKey);
    if (auditLogListRef.value) {
      auditLogListRef.value.showLatest();
    }
  } catch (e) {
    if (!demoteIfLockRejected(e)) {
      errors.value = tryParseApiErrors(e);
    }
  }
};

const onOutgoingComplete = async ({ sent, queuePayload }, { buildPrescriberOutbound, successMessage }) => {
  if (!sent) {
    return;
  }
  if (currentFaxAction.value === "prescriber" && queuePayload) {
    await queuePrescriberNotification(buildPrescriberOutbound(queuePayload));
    return;
  }
  successNotificationMessage.value = successMessage;
  showSuccessNotification.value = true;
  if (auditLogListRef.value) {
    auditLogListRef.value.showLatest();
  }
  if (currentFaxAction.value === "plan") {
    errors.value = [];
    clearOrderInfoStorage();
    router.push({ name: "all-submissions" });
    window.scrollTo(0, 0);
  }
};

const onFaxComplete = (payload) =>
  onOutgoingComplete(payload, {
    buildPrescriberOutbound: buildPrescriberOutboundFromFax,
    successMessage: "Fax sent successfully!",
  });

const onEmailComplete = (payload) =>
  onOutgoingComplete(payload, {
    buildPrescriberOutbound: buildPrescriberOutboundFromEmail,
    successMessage: "Email sent successfully!",
  });

const showFaxEditorForAction = async (action) => {
  const submissionMethod = orderInfo.value?.submissionMethod;

  // Determine if fax editor should be shown based on submission method and action
  if (action === "plan" && submissionMethod !== "eFax") {
    // For "Send to plan", only show fax editor if submission method is eFax
    return;
  }

  currentFaxAction.value = action;

  if (action === "prescriber") {
    // The prescriber's own login preference (LAM-5263) wins over the order flag when they have set one.
    const prefersEmail =
      orderInfo.value?.prescriberPrefersEmail ?? orderInfo.value?.forwardResponsesToProviderByEmail ?? false;
    sendMethod.value = sendEmailEnabled.value && prefersEmail ? "email" : "fax";
    currentTemplate.value = await fetchPrescriberTemplate();
    // The provider login's confirmed fax wins over the order field (LAM-5263); same for email below.
    currentFaxNumber.value = orderInfo.value?.prescriberEffectiveFax || orderInfo.value?.providerContactFax || "";
    sendButtonText.value = "Send fax to prescriber";
    showFaxHelpText.value = false;

    currentEmailSubject.value = `Prior Authorization Request - ${orderInfo.value?.orderKey}`.trim();
    const effectiveEmail = orderInfo.value?.prescriberEffectiveEmail || orderInfo.value?.providerEmail;
    currentEmailAddresses.value = effectiveEmail ? [effectiveEmail] : [];
  } else if (action === "plan") {
    // "plan" only runs for eFax orders (checked above), and the fax/email toggle is hidden for
    // eFax orders (see template) — so this must always be the fax editor, regardless of whatever
    // sendMethod a prior "prescriber" action on this same page load may have left behind.
    sendMethod.value = "fax";
    // For "plan" action, no template - only filled form attachment
    currentTemplate.value = null;
    currentFaxNumber.value = orderInfo.value?.sendFaxFormTo || "";
    sendButtonText.value = "Send fax to payor";
    showFaxHelpText.value = true;

    currentEmailSubject.value = `Prior Authorization Submission - ${orderInfo.value?.orderKey}`.trim();

    // Pre-select filled form attachment for "Send to plan"
    preselectFilledFormAttachment();
  }

  showFaxEditor.value = true;
  showEmailEditor.value = sendEmailEnabled.value;

  // Wait for next tick to ensure component is rendered
  await nextTick();

  // Scroll to the editor component
  const editorRef = sendMethod.value === "email" ? outgoingEmailEditorRef : outgoingFaxEditorRef;
  if (editorRef.value?.$el) {
    editorRef.value.$el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

// The fax was refused because the signer's saved signature would fade out in transmission. Bring the
// signature pad back on screen so they can re-draw and press send again — the fax editor keeps
// whatever they had composed, so nothing is lost by failing here.
const focusSignatureSection = async () => {
  allFormStepsRef.value?.focusSignatureSection?.();
  await nextTick();
  document.getElementById("signature-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const handleSendToPrescriber = async () => {
  await showFaxEditorForAction("prescriber");
};

const handleSendToPlan = async () => {
  const submissionMethod = orderInfo.value?.submissionMethod;

  // Only intercept if submission method is eFax
  if (submissionMethod === "eFax") {
    await showFaxEditorForAction("plan");
  }
  // For other submission methods, submission proceeds normally in AllFormStepsView
};
</script>

<style lang="scss" scoped>
.detail-page__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #5e7898;
  cursor: pointer;
  padding: 4px 0;
  margin-bottom: 8px;
  transition: color 150ms;

  &:hover {
    color: #052e5e;
  }
}
</style>
